import type React from 'react';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { useTheme } from '@contexts/ThemeContext';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import {
  getJarvisControlStyle,
  jarvisControlClassName,
  renderFieldError,
  renderFieldLabel,
  type JarvisFieldSize,
} from '@components/jarvis-ui/jarvis-field-utils';
import { cn } from '@/lib/utils';

type SearchableSelectProps = {
  options: {
    value: string;
    label: React.ReactNode;
    key?: string;
    disabled?: boolean;
  }[];
  id: string;
  label?: string;
  labelWidget?: React.ReactNode;
  value: string;
  onChange: (val: string) => void;
  formatter?: (val: string) => string;
  error?: string;
  errorWidget?: React.ReactNode;
  loading?: boolean;
  placeholder?: string;
  onSearchChange?: (val: string) => void;
  initialSearchValue?: string;
  displayValue?: string;
  disabled?: boolean;
  /** Matches JarvisTextInput / JarvisSelect density; default sm (h-8). */
  size?: JarvisFieldSize;
  className?: string;
  classNames?: {
    root?: string;
    input?: string;
    error?: string;
  };
  /** Inline overrides merged over the Jarvis control style (colors, radius, size). */
  style?: React.CSSProperties;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  required?: boolean;
  name?: string;
  /** Shows an "x" in the input that empties the search text. */
  clearable?: boolean;
};

function toSearchLabel(label: React.ReactNode, value: string): string {
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  return value;
}

export default function SearchableSelect({
  id,
  label,
  labelWidget,
  onChange,
  options,
  value,
  error,
  errorWidget,
  loading,
  placeholder,
  onSearchChange,
  initialSearchValue,
  displayValue,
  disabled = false,
  required = false,
  size = 'sm',
  formatter,
  className,
  classNames,
  style,
  onBlur,
  onFocus,
  onKeyPress,
  name,
  clearable = false,
}: SearchableSelectProps) {
  const { colors: themeColors } = useTheme();
  const [search, setSearch] = useState(displayValue || initialSearchValue || '');

  useEffect(() => {
    setSearch(displayValue || initialSearchValue || '');
  }, [initialSearchValue, displayValue]);

  const debouncedOnSearch = useDebouncedCallback((val: string) => {
    onSearchChange?.(val);
  }, 300);

  const handleSearch = (val: string) => {
    const next = formatter ? formatter(val) : val;
    setSearch(next);
    debouncedOnSearch(next);
  };

  const comboboxOptions: ComboboxOption[] = options.map((option) => ({
    value: String(option.value),
    label: option.label,
    searchLabel: toSearchLabel(option.label, String(option.value)),
    key: option.key || `${id}-${option.value}`,
    disabled: option.disabled,
  }));

  const hasError = Boolean(errorWidget || error || (required && !value));
  const errorMessage = errorWidget ?? (required && !value ? 'Field ini wajib diisi' : error);

  const controlStyle = getJarvisControlStyle(themeColors, { hasError, style });

  // Only render an in-control label when one was actually provided.
  // AsyncSelect defaults label="" while HeaderForm uses an external
  // JarvisInputLabel — rendering an empty required label produced a second "*".
  const fieldLabel = labelWidget
    ? labelWidget
    : label
      ? renderFieldLabel({
          id,
          label,
          required,
          textColor: themeColors.text,
        })
      : null;

  return (
    <div className={cn('w-full', classNames?.root, className)}>
      {fieldLabel}

      <Combobox
        id={id}
        name={name}
        inputMode
        clearable={clearable}
        searchable={false}
        shouldFilter={false}
        options={comboboxOptions}
        value={value || null}
        onChange={(val) => onChange(val ?? '')}
        searchValue={search}
        onSearchChange={(val) => {
          handleSearch(val);
          if (!val) onChange('');
        }}
        loading={loading}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        emptyText="Tidak dapat menemukan"
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        aria-invalid={hasError || undefined}
        triggerClassName={cn(jarvisControlClassName(size, classNames?.input), 'w-full')}
        style={controlStyle}
        contentClassName="z-[4000]"
      />

      {typeof errorMessage === 'string' || typeof errorMessage === 'number' ? (
        renderFieldError(errorMessage, themeColors.error)
      ) : errorMessage ? (
        <div className={cn('mt-1 text-xs', classNames?.error)}>{errorMessage}</div>
      ) : null}
    </div>
  );
}
